import { Badge, Flex, Text } from '@mantine/core';
import PropTypes from 'prop-types';
import React from 'react';
import iconArrowLeft from '../../assets/ic_arrow_left.svg';
import Button from '../../components/elements/Button';
import { IconCircleCheckFilled } from '../../components/icons/IconCircleCheckFilled';
import IconDocDefault from '../../components/icons/IconDocDefault';
import styles from './detail.scoped.css';
import { parseHistoryDateTime } from './utils/historyDateTime';

function getActionLabel(actionType) {
  if (actionType === 'delete') {
    return 'Hapus Provinsi';
  }

  if (actionType === 'transaction') {
    return 'Provinsi Transaksi';
  }

  return 'Set Provinsi Transaksi';
}

function getActionBadgeStyles(actionType) {
  if (actionType === 'delete') {
    return { bg: '#FEE4E2', c: '#F04438' };
  }

  return { bg: '#D1FADF', c: '#12B76A' };
}

function getProvinceNames(provinceData) {
  if (!provinceData) return [];

  const provinceList = Array.isArray(provinceData) ? provinceData : [provinceData];

  return provinceList
    .map((province) => province?.provinceName)
    .filter(Boolean);
}

function isSameProvinceList(firstProvinceNames, secondProvinceNames) {
  if (!firstProvinceNames.length || !secondProvinceNames.length) return false;
  if (firstProvinceNames.length !== secondProvinceNames.length) return false;

  const compareProvinceNames = (firstName, secondName) =>
    firstName.localeCompare(secondName);
  const sortedFirstProvinceNames = [...firstProvinceNames].sort(compareProvinceNames);
  const sortedSecondProvinceNames = [...secondProvinceNames].sort(compareProvinceNames);

  return sortedFirstProvinceNames.every(
    (provinceName, index) => provinceName === sortedSecondProvinceNames[index]
  );
}

function getMetaText(item) {
  if (item.actionType === 'transaction') {
    return `Data tercatat oleh ${item.verifiedBy}, ${item.dateTime}`;
  }

  return `Data disubmit oleh ${item.verifiedBy}, ${item.dateTime}`;
}

function getDeleteTitleText(provinceText, dateTime) {
  if (!provinceText) {
    return '';
  }

  const { isCurrentMonth, monthLabel } = parseHistoryDateTime(dateTime);

  if (isCurrentMonth) {
    return `Provinsi Transaksi ${provinceText} dihapus pada bulan berjalan.`;
  }

  return `Provinsi Transaksi ${provinceText} dihapus pada bulan ${monthLabel}.`;
}

function getTransactionReasonText(item, badgeText) {
  if (badgeText) {
    return `Pelanggan tercatat oleh sistem bertransaksi di ${badgeText}`;
  }

  return item.reason || '-';
}

export default function HistoryProvinceLog({ history, onPreviewDocument }) {
  if (!history?.length) {
    return (
      <div className={styles.emptyHistory}>
        Belum ada riwayat hapus/set provinsi transaksi pelanggan.
      </div>
    );
  }

  return (
    <div className={styles.historyTimeline}>
      {history.map((item, index) => {
        const provinceNames = getProvinceNames(item.provinces);
        const currentProvinceNames = getProvinceNames(item.currentProvinces);
        const badgeText = provinceNames.join(' & ');
        const currentProvinceText = currentProvinceNames.join(' & ');
        const isDelete = item.actionType === 'delete';
        const isTransaction = item.actionType === 'transaction';
        const isDeletedAllCurrentProvinces = isSameProvinceList(
          provinceNames,
          currentProvinceNames
        );
        const hasProvinceDetails =
          isDelete && (badgeText || currentProvinceText) && !isDeletedAllCurrentProvinces;
        let primaryHistoryChild = (
          <div className={styles.historyChildArrowRow}>
            <img
              alt=""
              aria-hidden="true"
              className={styles.historyArrow}
              src={iconArrowLeft}
            />
            <div className={styles.historyChildBody}>
              <Text className={styles.historyChildTitle} style={{ fontWeight: 600 }}>
                Alasan
              </Text>
              <Text className={styles.historyChildText} style={{ color: '#667085' }}>
                {item.reason || '-'}
              </Text>
            </div>
          </div>
        );

        if (isTransaction) {
          primaryHistoryChild = (
            <div className={styles.historyChildArrowRow}>
              <img
                alt=""
                aria-hidden="true"
                className={styles.historyArrow}
                src={iconArrowLeft}
              />
              <div className={styles.historyChildBody}>
                <Text className={styles.historyChildTitle} style={{ fontWeight: 600 }}>
                  Alasan
                </Text>
                <Text className={styles.historyChildText} style={{ color: '#667085' }}>
                  {getTransactionReasonText(item, badgeText)}
                </Text>
              </div>
            </div>
          );
        } else if (hasProvinceDetails) {
          primaryHistoryChild = (
            <div className={styles.historyChildArrowRow}>
              <img
                alt=""
                aria-hidden="true"
                className={styles.historyArrow}
                src={iconArrowLeft}
              />
              <div className={styles.historyChildBody}>
                {badgeText ? (
                  <Text className={styles.historyChildTitle} style={{ fontWeight: 600 }}>
                    {getDeleteTitleText(badgeText, item.dateTime)}
                  </Text>
                ) : null}

                {currentProvinceText ? (
                  <Text className={styles.historyChildText} style={{ color: '#667085' }}>
                    Provinsi transaksi yang berlaku saat ini adalah{' '}
                    {currentProvinceText}
                  </Text>
                ) : null}
              </div>
            </div>
          );
        }
        const connectorClassName = [
          styles.historyConnector,
          index === history.length - 1 ? styles.historyConnectorLast : '',
          index === history.length - 1 && hasProvinceDetails
            ? styles.historyConnectorLastWithDetails
            : '',
        ].filter(Boolean).join(' ');

        return (
          <div className={styles.historyItem} key={`${item.dateTime}-${index}`}>
            <div className={styles.historyMarkerColumn}>
              <IconCircleCheckFilled className={styles.historyMarker} />
              <div className={connectorClassName} />
            </div>
            <div className={styles.historyContent}>
              <Flex align="center" gap="xs" wrap="wrap">
                <Text className={styles.historyTitle} style={{ fontWeight: 600 }}>
                  {getActionLabel(item.actionType)}
                </Text>
                {badgeText ? (
                  <Badge
                    bg={getActionBadgeStyles(item.actionType).bg}
                    c={getActionBadgeStyles(item.actionType).c}
                    fw={500}
                    fz={14}
                    radius="xl"
                    tt="none"
                  >
                    {badgeText}
                  </Badge>
                ) : null}
              </Flex>
              <Text className={styles.historyMeta} style={{ color: '#667085' }}>
                {getMetaText(item)}
              </Text>

              <div className={styles.historyChild}>
                {primaryHistoryChild}

                {hasProvinceDetails ? (
                  <div className={styles.historyChildArrowRow}>
                    <img
                      alt=""
                      aria-hidden="true"
                      className={styles.historyArrow}
                      src={iconArrowLeft}
                    />
                    <div className={styles.historyChildBody}>
                      <Text className={styles.historyChildTitle} style={{ fontWeight: 600 }}>
                        Alasan
                      </Text>
                      <Text className={styles.historyChildText} style={{ color: '#667085' }}>
                        {item.reason || '-'}
                      </Text>
                    </div>
                  </div>
                ) : null}

                {item.supportDocument ? (
                  <Button
                    className={styles.documentButton}
                    onClick={() => onPreviewDocument(item.supportDocument)}
                    style="primary-500"
                  >
                    <IconDocDefault className={styles.documentButtonIcon} />
                    Lihat Dokumen Pendukung
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

HistoryProvinceLog.propTypes = {
  history: PropTypes.arrayOf(
    PropTypes.shape({
      actionType: PropTypes.string,
      dateTime: PropTypes.string,
      provinces: PropTypes.arrayOf(
        PropTypes.shape({
          provinceId: PropTypes.string,
          provinceName: PropTypes.string,
        })
      ),
      currentProvinces: PropTypes.arrayOf(
        PropTypes.shape({
          provinceId: PropTypes.string,
          provinceName: PropTypes.string,
        })
      ),
      reason: PropTypes.string,
      supportDocument: PropTypes.string,
      verifiedBy: PropTypes.string,
    })
  ),
  onPreviewDocument: PropTypes.func.isRequired,
};

HistoryProvinceLog.defaultProps = {
  history: [],
};
